// Staff sign-in: email+password against POST /api/auth/login, next to the
// Google button. The server answers one generic 401 for every rejection on
// purpose, and the copy here must not editorialize past it.
//
// Surfaces backend rejection reasons via ?error= query (e.g.
// ?error=domain_not_allowed when a Google account outside the firm's
// Workspace tries to sign in).

import { useMemo, useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { GoogleSignInButton } from "../components/auth/GoogleSignInButton";
import { AuthBrand } from "../components/auth/AuthBrand";
import {
  postMfaCode,
  requestMfaEmailCode,
  requestStaffPasswordLink,
  STORAGE_REFUSED_MESSAGE,
  safeReturnTo,
  staffLogin,
  storeToken,
  TOKEN_STORAGE_KEY,
  type StaffMfaChallenge,
} from "../api/auth";

const ERROR_MESSAGES: Record<string, string> = {
  domain_not_allowed:
    "Sign-in is restricted to your firm's Google Workspace accounts. Contact your administrator if you need access.",
  email_not_verified: "Your Google email isn't verified. Please verify it in Google and try again.",
  state_mismatch: "Your sign-in session expired. Please try again.",
  account_inactive: "Your account is inactive. Contact your administrator.",
  missing_token: "Sign-in failed. Please try again.",
  missing_code: "Google did not return an authorization code. Please try again.",
  token_exchange_failed: "Google rejected the sign-in. Please try again.",
  storage_failed: STORAGE_REFUSED_MESSAGE,
};

const FORGOT_MESSAGES = {
  mail_unavailable:
    "Password reset by email is not available right now. Sign in with Google, or call the office and we will help.",
  rate_limited: "Too many attempts. Please wait a minute and try again.",
  request_failed: "Could not reach the server. Please try again.",
};

const CODE_SEND_MESSAGES = {
  restart: "The sign-in step expired. Start over from your password.",
  throttled: "Too many codes requested. Wait about ten minutes and try again.",
  mail_unavailable:
    "Sign-in codes by email are not available right now. Call the office and an administrator will help.",
  request_failed: FORGOT_MESSAGES.request_failed,
};

type Mode = "login" | "forgot" | "forgot-sent" | "code";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryError = params.get("error");
  const returnTo = safeReturnTo(params.get("return_to"));

  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // The pending token lives here and nowhere else - RequireAuth must
  // never mistake it for a session.
  const [mfa, setMfa] = useState<StaffMfaChallenge | null>(null);
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);

  const queryMessage = useMemo(() => {
    if (!queryError) return null;
    return ERROR_MESSAGES[queryError] ?? `Sign-in failed: ${queryError}`;
  }, [queryError]);

  // The query errors are all about the sign-in door; they stay behind when
  // the user walks into the reset form.
  const banner = formError ?? (mode === "login" ? queryMessage : null);

  function completeSignIn(token: string) {
    if (!storeToken(token)) {
      setFormError(ERROR_MESSAGES.storage_failed);
      return;
    }
    navigate(returnTo, { replace: true });
  }

  async function onLogin(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const result = await staffLogin(email, password);
      if (!result.ok) {
        setFormError(result.message);
        return;
      }
      if ("mfa" in result) {
        // Login mode always leaves the code-step state clean (switchMode
        // is the single reset site), so only the challenge lands here.
        setMfa(result.mfa);
        setMode("code");
        return;
      }
      completeSignIn(result.token);
    } catch {
      setFormError(FORGOT_MESSAGES.request_failed);
    } finally {
      setBusy(false);
    }
  }

  async function onCode(e: FormEvent) {
    e.preventDefault();
    if (!mfa) return;
    setBusy(true);
    setFormError(null);
    try {
      const result = await postMfaCode(mfa.pendingToken, code);
      if (!result.ok) {
        setFormError(result.message);
        return;
      }
      // The code step never answers with a fresh challenge.
      if ("mfa" in result) return;
      completeSignIn(result.token);
    } catch {
      setFormError(FORGOT_MESSAGES.request_failed);
    } finally {
      setBusy(false);
    }
  }

  async function onSendCode() {
    if (!mfa) return;
    setBusy(true);
    setFormError(null);
    try {
      const result = await requestMfaEmailCode(mfa.pendingToken);
      if (result.ok) {
        setCodeSent(true);
      } else if (result.reason === "restart") {
        // The pending step is dead; make the copy true and walk back.
        switchMode("login");
        setFormError(CODE_SEND_MESSAGES.restart);
      } else {
        setFormError(CODE_SEND_MESSAGES[result.reason]);
      }
    } catch {
      setFormError(FORGOT_MESSAGES.request_failed);
    } finally {
      setBusy(false);
    }
  }

  async function onForgot(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const result = await requestStaffPasswordLink(email);
      if (result.ok) {
        setMode("forgot-sent");
      } else {
        setFormError(FORGOT_MESSAGES[result.reason]);
      }
    } catch {
      setFormError(FORGOT_MESSAGES.request_failed);
    } finally {
      setBusy(false);
    }
  }

  function switchMode(next: Mode) {
    if (next === "login") {
      setMfa(null);
      setCode("");
      setCodeSent(false);
      setUseRecovery(false);
    }
    setMode(next);
    setFormError(null);
  }

  // A stale token bounces back off RequireAuth's /me probe with the token
  // cleared, so this cannot loop.
  if (typeof window !== "undefined" && window.localStorage.getItem(TOKEN_STORAGE_KEY)) {
    return <Navigate to={returnTo} replace />;
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <div className="auth-head">
          <AuthBrand />
          <div className="auth-sub">
            {mode === "login"
              ? "Sign in to continue"
              : mode === "code"
                ? "Finish signing in"
                : "Reset your password"}
          </div>
        </div>

        {banner ? (
          <div role="alert" className="auth-alert">
            {banner}
          </div>
        ) : null}

        {mode === "login" ? (
          <>
            <form className="auth-form" onSubmit={onLogin}>
              <label className="auth-field">
                <span>Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label className="auth-field">
                <span>Password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
              <button type="submit" className="auth-submit" disabled={busy}>
                {busy ? "Signing in..." : "Sign in"}
              </button>
            </form>
            <button type="button" className="auth-link-btn" onClick={() => switchMode("forgot")}>
              Forgot or never set a password?
            </button>
            <div className="auth-divider">
              <span>or</span>
            </div>
            <GoogleSignInButton returnTo={returnTo} />
            <div className="auth-note">
              Use your firm's Google Workspace account, or the email and password set up for you.
            </div>
          </>
        ) : null}

        {mode === "code" && mfa ? (
          <>
            {mfa.method === "email" ? (
              <>
                <div className="auth-note">
                  We will email a one-time code to your address on file.
                </div>
                <button
                  type="button"
                  className="auth-link-btn"
                  onClick={onSendCode}
                  disabled={busy}
                >
                  {codeSent ? "Email me a new code" : "Email me a code"}
                </button>
                {codeSent ? (
                  <p role="status" className="auth-ok">
                    A code is on its way. It works once and expires in 10 minutes; asking for a new
                    one replaces it.
                  </p>
                ) : null}
              </>
            ) : null}
            <form className="auth-form" onSubmit={onCode}>
              <label className="auth-field">
                <span>
                  {useRecovery
                    ? "Recovery code"
                    : mfa.method === "email"
                      ? "Code from the email"
                      : "Code from your authenticator app"}
                </span>
                <input
                  type="text"
                  inputMode={useRecovery ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </label>
              <button type="submit" className="auth-submit" disabled={busy}>
                {busy ? "Checking..." : "Sign in"}
              </button>
            </form>
            {mfa.method === "totp" ? (
              <button
                type="button"
                className="auth-link-btn"
                onClick={() => setUseRecovery(!useRecovery)}
              >
                {useRecovery ? "Use the authenticator app instead" : "Use a recovery code instead"}
              </button>
            ) : null}
            <button type="button" className="auth-link-btn" onClick={() => switchMode("login")}>
              Back to sign-in
            </button>
          </>
        ) : null}

        {mode === "forgot" ? (
          <>
            <form className="auth-form" onSubmit={onForgot}>
              <label className="auth-field">
                <span>Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <button type="submit" className="auth-submit" disabled={busy}>
                {busy ? "Sending..." : "Email me a link"}
              </button>
            </form>
            <button type="button" className="auth-link-btn" onClick={() => switchMode("login")}>
              Back to sign-in
            </button>
          </>
        ) : null}

        {mode === "forgot-sent" ? (
          <>
            {/* Says nothing about whether that address is on staff - the
                server refuses to, and so does this screen. */}
            <p role="status" className="auth-ok">
              If that address is on file with us, a link is on its way. It works once and expires in
              24 hours.
            </p>
            <button type="button" className="auth-link-btn" onClick={() => switchMode("login")}>
              Back to sign-in
            </button>
          </>
        ) : null}
      </div>
    </main>
  );
}
